import { useQuery, useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { useState, useCallback, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Database,
  ArrowUp,
  ArrowDown,
  Users,
  CheckCircle2,
  TrendingUp,
  ArrowUpDown,
  Check,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import EventCard from '@/components/event/EventCard';
import { useSearchParams, useRouter } from 'next/navigation';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

const ITEMS_PER_PAGE = 9;

const sortOptions = [
  { value: 'date_desc', label: 'Latest', Icon: ArrowDown },
  { value: 'date_asc', label: 'Oldest', Icon: ArrowUp },
  { value: 'price_asc', label: 'Price: Low-High', Icon: ArrowUp },
  { value: 'price_desc', label: 'Price: High-Low', Icon: ArrowDown },
  { value: 'leads_desc', label: 'Most Leads', Icon: Users },
  { value: 'sold_desc', label: 'Best Selling', Icon: CheckCircle2 },
  { value: 'trending', label: 'Trending', Icon: TrendingUp },
];

export default function LeadListsSection() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(1);

  const searchTerm = searchParams?.get('q') || undefined;
  const category = searchParams?.get('category') || undefined;
  const sortBy = searchParams?.get('sort') || 'date_desc';

  const searchConfig = {
    searchTerm: searchTerm || undefined,
    category: category || undefined,
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    sortBy: sortBy,
  };

  const result = useQuery(api.events.getWithFilters, searchConfig);

  const handlePageChange = useCallback(
    (newPage: number) => {
      const lastPageChange = sessionStorage.getItem('lastPageChange');
      const now = Date.now();
      if (lastPageChange && now - parseInt(lastPageChange, 10) < 1000) {
        return;
      }

      sessionStorage.setItem('lastPageChange', now.toString());

      const maxPage = result?.totalPages || 1;
      const safeNewPage = Math.min(Math.max(1, newPage), maxPage);

      setCurrentPage(safeNewPage);
    },
    [result?.totalPages],
  );

  const handleSortChange = (value: string) => {
    if (value === sortBy) {
      return;
    }

    const params = new URLSearchParams(searchParams?.toString() || '');
    if (value && value !== 'date_desc') {
      params.set('sort', value);
    } else {
      params.delete('sort');
    }
    router.push(`?${params.toString()}`);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchParams?.get('q'), searchParams?.get('category'), searchParams?.get('sort')]);

  return (
    <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
      <div className="space-y-4 sm:space-y-6 lg:space-y-8 animate-fade-in" id="lead-lists">
        <div className="bg-card p-6 sm:p-8 rounded-xl border border-[hsl(var(--border))] shadow-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-[hsl(var(--border))] pb-4 mb-6 gap-4">
            <div>
              <h2 className="text-2xl font-semibold text-foreground">Find a Lead List</h2>
              <p className="mt-1 text-muted-foreground">
                {result?.totalCount || 0} available lists
              </p>
            </div>
            <div className="w-full sm:w-auto">
              {/* Dropdown for small screens */}
              <div className="sm:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      Sort by:{' '}
                      {sortOptions.find((opt) => opt.value === sortBy)?.label || 'Select...'}
                      <ArrowUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-card">
                    {sortOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className={sortBy === option.value ? 'bg-accent/15 text-accent' : ''}
                      >
                        <option.Icon className="mr-2 h-4 w-4" />
                        <span>{option.label}</span>
                        {sortBy === option.value && <Check className="ml-auto h-4 w-4" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Button row for larger screens */}
              <div className="hidden sm:flex flex-wrap items-center gap-x-4 gap-y-2 pb-2">
                {/* Group 1: Date */}
                <div className="flex items-center gap-2">
                  {sortOptions.slice(0, 2).map((option) => (
                    <Button
                      key={option.value}
                      variant={sortBy === option.value ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => handleSortChange(option.value)}
                      className="flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <option.Icon className="w-3.5 h-3.5" />
                      <span>{option.label}</span>
                    </Button>
                  ))}
                </div>

                {/* Group 2: Price */}
                <div className="flex items-center gap-2">
                  {sortOptions.slice(2, 4).map((option) => (
                    <Button
                      key={option.value}
                      variant={sortBy === option.value ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => handleSortChange(option.value)}
                      className="flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <option.Icon className="w-3.5 h-3.5" />
                      <span>{option.label}</span>
                    </Button>
                  ))}
                </div>

                {/* Group 3: Other */}
                <div className="flex items-center gap-2">
                  {sortOptions.slice(4).map((option) => (
                    <Button
                      key={option.value}
                      variant={sortBy === option.value ? 'primary' : 'outline'}
                      size="sm"
                      onClick={() => handleSortChange(option.value)}
                      className="flex items-center gap-1.5 whitespace-nowrap"
                    >
                      <option.Icon className="w-3.5 h-3.5" />
                      <span>{option.label}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Conditional Rendering */}
          {!result ? (
            <div className="flex justify-center items-center min-h-[300px] pt-2 pb-16 -mt-16">
              <div className="w-10 h-10 -mt-10">
                <LoadingSpinner />
              </div>
            </div>
          ) : result.events.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {result.events.map((event) => (
                <EventCard key={event._id} eventId={event._id} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Database className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                No matching lead lists found
              </h3>
              <p className="text-muted-foreground">
                Try broadening your search criteria or using different categories
              </p>
            </div>
          )}
        </div>

        {result && result.totalPages > 1 && (
          <div className="flex justify-center gap-3 pt-8 pb-4">
            <Button
              variant="outline"
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {result.totalPages}
              </span>
            </div>

            <Button
              variant="outline"
              onClick={() => handlePageChange(Math.min(result.totalPages, currentPage + 1))}
              disabled={currentPage === result.totalPages}
              className="flex items-center gap-2"
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
